import { Link } from '@tanstack/react-router';
import { ChartColumn, KeyRound, ShieldCheck, Store } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { LilyLogo } from '~/components/logos/LilyLogo';
import { Button } from '~/components/ui/button';
import { GOOGLE_API_USER_DATA_POLICY_URL, PRIVACY_POLICY_URL } from '~/constants/config/constants';
import { APP_NAME } from '~/constants/config/env';
import { ProductSpecimen } from '~/features/public-home/components/product-specimen';

/** Product capabilities, read left to right — i18n keys under `home.features`. */
const FEATURES = ['enrich', 'feeds', 'experiments'] as const;

/** Google products the app connects to — i18n keys under `home.googleData.items`. */
const GOOGLE_DATA_ITEMS: { key: string; icon: LucideIcon }[] = [
  { key: 'signIn', icon: KeyRound },
  { key: 'merchantCenter', icon: Store },
  { key: 'ads', icon: ChartColumn },
];

/**
 * Public, sign-in-free homepage at `/`. Exists for Google OAuth brand verification:
 * it shows the app name exactly as the consent screen does (`APP_NAME`), explains what
 * the product does and why it requests Google data, and links the privacy policy.
 */
export function PublicHomePage() {
  const { t } = useTranslation('auth');

  // The static <title> is already stamped at build time; re-set it in case a previous
  // route changed it during this SPA session.
  useEffect(() => {
    document.title = APP_NAME;
  }, []);

  // `html { overflow: hidden }` is global (styles/tailwind/base.css), so the page owns
  // its own scroll container at viewport height.
  return (
    <div className="bg-neutral-30 text-foreground h-dvh overflow-y-auto">
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col px-4 sm:px-8">
        <header className="flex items-center justify-between gap-4 pt-4">
          <LilyLogo className="-ml-3 h-14 w-auto sm:h-16" />
          <Button asChild className="cursor-pointer px-6 shadow-sm">
            <Link to="/login">{t('home.signIn')}</Link>
          </Button>
        </header>

        <main className="flex flex-1 flex-col">
          {/* Hero: the product name set large and light, against a specimen of what the
              product actually does — a thin feed record becoming a rich one. */}
          <section className="grid items-center gap-12 py-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:py-20">
            <div className="max-w-xl">
              <h1 className="text-[clamp(3rem,7vw,5.5rem)] leading-[0.95] font-extralight tracking-[-0.04em]">
                {APP_NAME}
              </h1>
              <p className="mt-6 text-2xl leading-snug font-medium tracking-tight sm:text-[1.75rem]">
                {t('home.tagline')}
              </p>
              <p className="text-muted-foreground mt-5 text-lg leading-relaxed">
                {t('home.description', { appName: APP_NAME })}
              </p>
              <Button asChild size="lg" className="mt-9 cursor-pointer">
                <Link to="/login">{t('home.getStarted', { appName: APP_NAME })}</Link>
              </Button>
            </div>

            {/* One soft brand glow behind the specimen — the hero's single focal point. */}
            <div className="relative">
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-x-10 -inset-y-12 bg-[radial-gradient(closest-side,var(--gradient-promo-pink),transparent),radial-gradient(closest-side_at_20%_80%,var(--gradient-promo-lavender),transparent)] opacity-70 blur-2xl"
              />
              <ProductSpecimen />
            </div>
          </section>

          <section
            aria-labelledby="home-features-heading"
            className="border-foreground/10 border-t py-14"
          >
            <h2
              id="home-features-heading"
              className="text-3xl font-light tracking-tight sm:text-4xl"
            >
              {t('home.featuresHeading')}
            </h2>
            <div className="mt-10 grid gap-10 md:grid-cols-3 md:gap-8">
              {FEATURES.map((key) => (
                <div key={key} className="border-brand border-l-2 pl-5">
                  <h3 className="text-lg font-semibold">{t(`home.features.${key}.title`)}</h3>
                  <p className="text-muted-foreground mt-2 leading-relaxed">
                    {t(`home.features.${key}.body`)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Required disclosure for Google OAuth verification: per Google product, what
              is accessed and why, then the no-sale / Limited Use commitment. */}
          <section
            aria-labelledby="home-google-data-heading"
            className="border-foreground/10 border-t py-14"
          >
            <div className="grid gap-4 lg:grid-cols-2 lg:items-end lg:gap-16">
              <h2
                id="home-google-data-heading"
                className="text-3xl font-light tracking-tight text-balance sm:text-4xl"
              >
                {t('home.googleData.heading', { appName: APP_NAME })}
              </h2>
              <p className="text-muted-foreground max-w-xl leading-relaxed">
                {t('home.googleData.intro', { appName: APP_NAME })}
              </p>
            </div>

            <div className="bg-card mt-10 overflow-hidden rounded-3xl border">
              <dl className="divide-border grid divide-y md:grid-cols-3 md:divide-x md:divide-y-0">
                {GOOGLE_DATA_ITEMS.map(({ key, icon: Icon }) => (
                  <div key={key} className="flex flex-col p-6 sm:p-7">
                    <dt className="flex items-center gap-3 text-lg font-semibold">
                      <Icon className="text-brand h-5 w-5 shrink-0" aria-hidden />
                      {t(`home.googleData.items.${key}.title`)}
                    </dt>
                    <dd className="mt-5 flex flex-col gap-4">
                      <div>
                        <p className="text-muted-foreground text-sm">
                          {t('home.googleData.accessLabel')}
                        </p>
                        <p className="mt-1 leading-relaxed">
                          {t(`home.googleData.items.${key}.access`)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">
                          {t('home.googleData.useLabel')}
                        </p>
                        <p className="mt-1 leading-relaxed">
                          {t(`home.googleData.items.${key}.use`, { appName: APP_NAME })}
                        </p>
                      </div>
                    </dd>
                  </div>
                ))}
              </dl>

              {/* The commitment is part of the disclosure, so it closes the same panel. */}
              <div className="bg-brand-100/60 flex items-start gap-4 border-t px-6 py-6 sm:px-7">
                <ShieldCheck className="text-brand h-6 w-6 shrink-0" aria-hidden />
                <div className="max-w-3xl">
                  <p className="font-semibold">{t('home.googleData.commitment')}</p>
                  <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                    {t('home.googleData.limitedUse', { appName: APP_NAME })}{' '}
                    <a
                      href={GOOGLE_API_USER_DATA_POLICY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground focus-visible:ring-ring cursor-pointer rounded-sm underline underline-offset-4 focus-visible:ring-2 focus-visible:outline-none"
                    >
                      {t('home.googleData.policyLink')}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="border-foreground/10 text-muted-foreground flex flex-col items-start justify-between gap-3 border-t py-6 text-sm sm:flex-row sm:items-center">
          <span>{t('home.copyright', { year: new Date().getFullYear() })}</span>
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground focus-visible:ring-ring cursor-pointer rounded-sm underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
          >
            {t('login.privacyPolicy')}
          </a>
        </footer>
      </div>
    </div>
  );
}
